import { resolvePlayerSearchQuery } from './playerSearchQueryMode';

// Every signal here only re-orders the results; none of them can remove a player. An id lookup
// skips the lot: that request wants one exact player, and the pin in particular would delete it
// while the server still turns a geo sort into a hard 200mi filter (UT-11651) — filter=id:3945798
// answers total 1 alone and total 0 once pin and sort are added.
const appendRankingSignals = (params, { isProximitySearch, clubLocation, boostLocality, ageBoost }) => {
    if (isProximitySearch) {
        params.append('pin', `${clubLocation.latLng[0]}, ${clubLocation.latLng[1]}`);
        // our elastic index only has lngLat as a geo_point mapping
        params.append('sort', 'location.lngLat');
    }

    // The gauss decay on the pin has a 100mi scale, so it separates nothing across a state:
    // measured on prod, a Dallas school's nearest exact "Mark Smith" was 350 miles away in
    // Lubbock and still ranked behind a UK player. City and state give the ordering Product
    // asked for (UT-11653).
    //
    // These two are gated on `boostLocality`, NOT on `isProximitySearch`. The add-members page
    // renders for `clubs|schools|colleges` (routes.jsx) and sets `isProximitySearch` for all of
    // them, so keying on it would re-order a college's and a plain club's search as well —
    // against this ticket's own acceptance criterion. The caller decides, exactly as it already
    // does for `ageBoost`. The pin and the geo sort predate this ticket and stay as they were.
    if (boostLocality && clubLocation?.cityName) {
        params.append('nearCityName', clubLocation.cityName);
    }

    if (boostLocality && clubLocation?.stateName) {
        params.append('nearStateName', clubLocation.stateName);
    }

    // Both bounds are required: the server ignores a half-open band, so sending one alone would
    // be a silent no-op that reads like a working feature.
    if (ageBoost?.min != null && ageBoost?.max != null) {
        params.append('ageBoostMin', ageBoost.min);
        params.append('ageBoostMax', ageBoost.max);
    }
};

/**
 * Builds the query string for `GET v2/search/players`.
 *
 * @param {Object} args
 * @param {string} args.query - the raw text the user typed
 * @param {number} args.skip - paging offset
 * @param {boolean} [args.searchMembersOnly]
 * @param {boolean} [args.showDemoPlayers]
 * @param {boolean} [args.excludeProPlayers]
 * @param {boolean} [args.excludeNonEligiblePlayers]
 * @param {number}  [args.eventId]
 * @param {boolean} [args.claimed]
 * @param {number}  [args.clubSubTypeId]
 * @param {boolean} [args.isProximitySearch]
 * @param {Object}  [args.clubLocation]
 * @param {boolean} [args.boostLocality] - send the club's city and state as ranking signals
 * @param {Object}  [args.ageBoost] - {min, max}; both bounds required, or nothing is sent
 * @param {Object}  [args.searchParams] - extra caller-supplied parameters
 * @returns {URLSearchParams}
 */
export const buildPlayerSearchParams = ({
    query,
    skip,
    searchMembersOnly,
    showDemoPlayers,
    excludeProPlayers,
    excludeNonEligiblePlayers,
    eventId,
    claimed,
    clubSubTypeId,
    isProximitySearch,
    clubLocation,
    boostLocality,
    ageBoost,
    searchParams,
}) => {
    const { isPlayerIdLookup, playerId } = resolvePlayerSearchQuery(query);

    const params = new URLSearchParams();

    if (isPlayerIdLookup) {
        params.append('filter', `id:${playerId}`);
    } else {
        params.append('query', query);
    }

    params.append('top', 10);
    params.append('skip', skip);

    if (searchMembersOnly) {
        params.append('exists', 'memberId');
    }

    params.append('isDemoPlayer', !!showDemoPlayers);

    if (excludeProPlayers) {
        params.append('filter', 'isPro:false');
    }

    if (excludeNonEligiblePlayers) {
        params.append('eligibleForTmsEventId', eventId);
    }

    params.append('claimed', !!claimed);

    if (clubSubTypeId === 1) {
        params.append('gender', 'M');
    } else if (clubSubTypeId === 2) {
        params.append('gender', 'F');
    }

    if (!isPlayerIdLookup) {
        appendRankingSignals(params, { isProximitySearch, clubLocation, boostLocality, ageBoost });
    }

    if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(entry => params.append(key, entry));
            } else {
                params.append(key, value);
            }
        });
    }

    return params;
};
